import { Button, CircularProgress, Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import { Theme } from "@material-ui/core";
import React, { useState, ReactNode } from "react";
import { StaticImage } from "gatsby-plugin-image";
import testimonials from "../../data/dispatchTestimonials.json";

interface NewsletterRowProps {
  invertColors?: boolean;
  totalUsers?: number;
  tagline?: ReactNode;
  contextType?: "faq" | "catalog" | "ifttt" | "library" | "newsletter" | "default";
}

const CONTEXT_TAGLINES: Record<string, string> = {
  faq: "Get weekly Compose tips & answers delivered to your inbox",
  catalog: "Be first to discover new Compose libraries every week",
  ifttt: "Master Compose migration with weekly tips from experts",
  library: "Stay updated on the best Compose libraries & tools",
  newsletter: "Join thousands of Android devs who look forward to Dispatch",
  default: "",
};

export default function NewsletterRow({
  invertColors = false,
  totalUsers,
  tagline,
  contextType = "default",
}: NewsletterRowProps) {
  const contextTagline = CONTEXT_TAGLINES[contextType];
  const classes = useStyles({ invertColors });
  const [signupSuccessful, setSignupSuccessful] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [randomTestimonial] = useState(() => {
    const randomIndex = Math.floor(Math.random() * testimonials.length);
    return testimonials[randomIndex];
  });

  const handleSubmit = async (event: any): Promise<void> => {
    event.preventDefault();
    if (!email) {
      setError("Please enter your email address");
      return;
    }
    if (!validEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }
    try {
      const currentUrl = window.location.href;
      setLoading(true);
      setError(null);
      const formData = {
        email: email,
        referringSite: currentUrl,
      };
      const response = await fetch("/api/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });
      const result = await response.json();
      if (
        result &&
        result.data &&
        (result.data.status === "validating" || result.data.status === "active")
      ) {
        setTimeout(() => {
          setSignupSuccessful(true);
          setLoading(false);
          setError(null);
        }, 2000);
      } else {
        setError("Something went wrong. Please try again.");
        setLoading(false);
      }
    } catch (error) {
      setError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  function validEmail(email: string): boolean {
    const re =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
  }

  return (
    <div className={classes.newsletterRoot}>
      {loading ? (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      ) : !signupSuccessful ? (
        <Grid container>
          <Grid item xs={12}>
            <div className={classes.logoContainer}>
              <StaticImage
                src="../../images/newsletter/dispatch-simple-logo.png"
                alt="Dispatch Newsletter"
                className={classes.dispatchLogo}
                placeholder="blurred"
                height={80}
              />
            </div>
            <div className={classes.description}>
              {tagline ? (
                tagline
              ) : contextTagline ? (
                contextTagline
              ) : (
                <>
                  Join {totalUsers ? totalUsers : "thousands of"} Android
                  developers getting the most entertaining & insightful Jetpack
                  Compose newsletter
                </>
              )}
            </div>
            <div className={classes.featuresGrid}>
              <div className={classes.featureCard}>
                <div className={classes.featureIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className={classes.featureContent}>
                  <div className={classes.featureTitle}>Curated Insights</div>
                  <div className={classes.featureDescription}>Digest in 5 minutes or less</div>
                </div>
              </div>
              <div className={classes.featureCard}>
                <div className={classes.featureIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14.5 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V7.5L14.5 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M12 18V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M9 15L12 12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className={classes.featureContent}>
                  <div className={classes.featureTitle}>Android Analysis</div>
                  <div className={classes.featureDescription}>Entertaining takes on happenings</div>
                </div>
              </div>
              <div className={classes.featureCard}>
                <div className={classes.featureIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2Z" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </div>
                <div className={classes.featureContent}>
                  <div className={classes.featureTitle}>Insider Tips</div>
                  <div className={classes.featureDescription}>From top Android developers</div>
                </div>
              </div>
              <div className={classes.featureCard}>
                <div className={classes.featureIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className={classes.featureContent}>
                  <div className={classes.featureTitle}>Hidden Gems</div>
                  <div className={classes.featureDescription}>You won't find elsewhere</div>
                </div>
              </div>
            </div>
            <div className={classes.testimonial}>
              <div className={classes.quoteDecoration}>"</div>
              <div className={classes.testimonialText}>
                "{randomTestimonial.testimonial}"
              </div>
              <div className={classes.testimonialAuthor}>
                — {randomTestimonial.name}
              </div>
              <div className={classes.testimonialRole}>
                {randomTestimonial.username}
              </div>
            </div>
            <div className={classes.formContainer}>
              <div className={classes.inputRow}>
                <TextField
                  id="newsletter-row-email-address"
                  placeholder="Enter your email"
                  variant="outlined"
                  className={classes.emailField}
                  error={Boolean(error)}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError(null);
                  }}
                  InputProps={{
                    className: classes.inputProps,
                  }}
                />
                <Button
                  variant="contained"
                  className={classes.subscribeButton}
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  Subscribe
                </Button>
              </div>
              {error && <div className={classes.errorMessage}>{error}</div>}
            </div>
            <div className={classes.subText}>
              Join thousands of Android devs who look forward to Dispatch every
              week
            </div>
          </Grid>
        </Grid>
      ) : (
        <div className={classes.successMessage}>
          🎉 Thank you for joining us on the journey! Keep an eye out for an
          email from us 🎉
        </div>
      )}
    </div>
  );
}

export const useStyles = makeStyles<Theme, { invertColors: boolean }>(
  (theme: Theme) => ({
    newsletterRoot: {
      background: (props) => (props.invertColors ? "#222" : "#D6EFFE"),
      borderRadius: 8,
      padding: 32,
      marginLeft: 32,
      marginRight: 32,
      marginBottom: 32,
      maxWidth: 800,
    },
    logoContainer: {
      display: "flex",
      justifyContent: "center",
      marginBottom: 24,
    },
    dispatchLogo: {
      height: 80,
      width: "auto",
    },
    description: {
      fontFamily: "Nunito Sans",
      fontWeight: 400,
      fontSize: 20,
      color: (props) => (props.invertColors ? "#FFF" : "#222"),
      textAlign: "center",
      marginBottom: 24,
      lineHeight: 1.5,
    },
    featuresGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, 1fr)",
      gap: 16,
      marginBottom: 28,
      maxWidth: 600,
      margin: "0 auto 28px",
      [theme.breakpoints.down("sm")]: {
        gridTemplateColumns: "1fr",
        gap: 12,
      },
    },
    featureCard: {
      display: "flex",
      alignItems: "flex-start",
      gap: 14,
      padding: "16px 18px",
      background: (props) => (props.invertColors ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.7)"),
      borderRadius: 12,
      border: (props) => (props.invertColors ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(70,54,247,0.08)"),
      transition: "all 0.2s ease",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: (props) => (props.invertColors ? "0 8px 24px rgba(0,0,0,0.3)" : "0 8px 24px rgba(70,54,247,0.12)"),
        background: (props) => (props.invertColors ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.95)"),
      },
      [theme.breakpoints.down("sm")]: {
        padding: "14px 16px",
      },
    },
    featureIcon: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: 44,
      height: 44,
      borderRadius: 10,
      background: (props) => (props.invertColors ? "rgba(70,54,247,0.3)" : "rgba(70,54,247,0.1)"),
      color: "#4636f7",
      flexShrink: 0,
      [theme.breakpoints.down("sm")]: {
        width: 40,
        height: 40,
      },
    },
    featureContent: {
      display: "flex",
      flexDirection: "column",
      gap: 4,
    },
    featureTitle: {
      fontFamily: "Inter, Nunito Sans, sans-serif",
      fontWeight: 600,
      fontSize: 15,
      color: (props) => (props.invertColors ? "#FFF" : "#1a1a2e"),
      letterSpacing: "-0.01em",
      [theme.breakpoints.down("sm")]: {
        fontSize: 14,
      },
    },
    featureDescription: {
      fontFamily: "Inter, Nunito Sans, sans-serif",
      fontWeight: 400,
      fontSize: 13,
      color: (props) => (props.invertColors ? "rgba(255,255,255,0.7)" : "#64748b"),
      lineHeight: 1.4,
      [theme.breakpoints.down("sm")]: {
        fontSize: 12,
      },
    },
    testimonial: {
      background: "#FFFFFF",
      borderRadius: 12,
      padding: "16px",
      marginBottom: 24,
      position: "relative",
      [theme.breakpoints.down("sm")]: {
        padding: "12px",
        marginBottom: 16,
      },
    },
    testimonialText: {
      fontFamily: "Inter",
      fontSize: 14,
      lineHeight: 1.5,
      color: "#374151",
      fontStyle: "italic",
      marginBottom: 12,
      [theme.breakpoints.down("sm")]: {
        fontSize: 12,
        marginBottom: 8,
      },
    },
    testimonialAuthor: {
      fontFamily: "Inter",
      fontSize: 14,
      fontWeight: 600,
      color: "#111827",
      [theme.breakpoints.down("sm")]: {
        fontSize: 12,
      },
    },
    testimonialRole: {
      fontFamily: "Inter",
      fontSize: 12,
      color: "#6B7280",
      [theme.breakpoints.down("sm")]: {
        fontSize: 11,
      },
    },
    quoteDecoration: {
      position: "absolute",
      top: 8,
      left: 8,
      fontSize: 32,
      color: "#4636f7",
      fontFamily: "Georgia",
      opacity: 0.1,
      lineHeight: 1,
    },
    formContainer: {
      marginTop: 24,
      marginBottom: 16,
    },
    inputRow: {
      display: "flex",
      justifyContent: "center",
      gap: 16,
      marginBottom: 8,
      [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
        alignItems: "center",
      },
    },
    emailField: {
      width: "100%",
      maxWidth: 400,
      "& .MuiOutlinedInput-root": {
        backgroundColor: "#FFF",
      },
    },
    inputProps: {
      color: "#222",
      backgroundColor: "#FFF",
    },
    subscribeButton: {
      backgroundColor: "#4636f7 !important",
      color: "#FFFFFF",
      padding: "12px 24px",
      height: "fit-content",
      "&:hover": {
        backgroundColor: "#3428b5 !important",
      },
      [theme.breakpoints.down("sm")]: {
        width: "100%",
        maxWidth: 400,
      },
    },
    errorMessage: {
      color: theme.palette.error.main,
      fontSize: 14,
      textAlign: "center",
      marginTop: 8,
    },
    subText: {
      fontFamily: "Nunito Sans",
      fontWeight: 400,
      fontSize: 14,
      color: (props) => (props.invertColors ? "#FFF" : "#222"),
      textAlign: "center",
      opacity: 0.8,
    },
    loading: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      minHeight: 200,
    },
    successMessage: {
      fontFamily: "Nunito Sans",
      fontWeight: 400,
      fontSize: 20,
      color: (props) => (props.invertColors ? "#FFF" : "#222"),
      textAlign: "center",
      padding: 32,
    },
  })
);
